import * as React from 'react';
import { Box, Typography, styled } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';

import HomeButton from './HomeButton';
import SearchButton from './SearchButton';
import GalleryButton from './GalleryButton';
import SettingButton from './SettingButton';
import MyFamilyTreeButton from './MyFamilyTreeButton';
import FindRelationButton from './FindRelationButton';
import FindPartnerButton from './FindPartnerButton';
import AdminDashboardButton from './AdminDashboardButton';
import AdminAddressButton from './AdminAddressButton';

// ======================================================
// Styled Components
// ======================================================

const SidebarSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ theme, isOpen }) => ({
  marginBottom: theme.spacing(1),

  [theme.breakpoints.down('sm')]: {
    marginBottom: 0,
  },

  ...(isOpen === false && {
    marginBottom: 0,
  }),
}));

const SectionTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ theme, isOpen }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  padding: theme.spacing(0, 2),
  marginBottom: theme.spacing(1),

  transition: 'opacity 0.3s ease',

  opacity: isOpen ? 1 : 0,
  height: isOpen ? 'auto' : 0,
  overflow: 'hidden',

  [theme.breakpoints.down('sm')]: {
    height: '8px',
    padding: theme.spacing(0, 1),
    visibility: 'hidden',
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  margin: theme.spacing(0.5, 1),

  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0.5, 0.5),

    '& .MuiListItemText-primary': {
      display: 'none',
    },

    '& .MuiListItemIcon-root': {
      minWidth: 'auto',
      marginRight: 0,
    },
  },

  '&:hover': {
    backgroundColor: theme.palette.primary.light,

    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },

    '& .MuiListItemText-primary': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },

  '&.Mui-selected': {
    backgroundColor: `${theme.palette.primary.light} !important`,

    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },

    '& .MuiListItemText-primary': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
}));

// ======================================================
// Main Sidebar Items
// ======================================================

export const MainListItems = ({ open }) => {
  const [isAdmin, setIsAdmin] = React.useState(
    localStorage.getItem('userRole') === '1'
  );

  React.useEffect(() => {
    const checkAdminStatus = () => {
      setIsAdmin(localStorage.getItem('userRole') === '1');
    };

    checkAdminStatus();

    window.addEventListener('storage', checkAdminStatus);

    return () => {
      window.removeEventListener('storage', checkAdminStatus);
    };
  }, []);

  return (
    <Box
      sx={{
        pt: 2,

        '& .MuiListItemButton-root': {
          px: { xs: 1, sm: 2 },
        },
      }}
    >
      {/* ==================================================
          ADMINISTRATION
      ================================================== */}

      {isAdmin && (
        <SidebarSection isOpen={open}>
          <SectionTitle isOpen={open}>
            Administration
          </SectionTitle>

          <AdminDashboardButton component={StyledListItemButton} />

          <AdminAddressButton component={StyledListItemButton} />
        </SidebarSection>
      )}

      {/* ==================================================
          MAIN NAVIGATION
      ================================================== */}

      <SidebarSection isOpen={open}>
        <SectionTitle isOpen={open}>
          Main Navigation
        </SectionTitle>

        <HomeButton component={StyledListItemButton} />

        <MyFamilyTreeButton component={StyledListItemButton} />
      </SidebarSection>

      {/* ==================================================
          SEARCH & DISCOVERY
      ================================================== */}

      <SidebarSection isOpen={open}>
        <SectionTitle isOpen={open}>
          Search & Discovery
        </SectionTitle>

        <SearchButton component={StyledListItemButton} />

        <FindRelationButton component={StyledListItemButton} />

        <FindPartnerButton component={StyledListItemButton} />
      </SidebarSection>

      {/* ==================================================
          MEDIA
      ================================================== */}

      <SidebarSection isOpen={open}>
        <SectionTitle isOpen={open}>
          Media
        </SectionTitle>

        <GalleryButton component={StyledListItemButton} />
      </SidebarSection>

      {/* ==================================================
          SETTINGS
      ================================================== */}

      <SidebarSection isOpen={open}>
        <SectionTitle isOpen={open}>
          Settings
        </SectionTitle>

        <SettingButton component={StyledListItemButton} />
      </SidebarSection>
    </Box>
  );
};